<script setup lang="ts">
  import { ref, watch, computed } from 'vue';
  import { PosDevicesService } from '@/api/services/pos-devices.service';
  import ModalsPosDevice from '@/components/modals/pos-device/ModalsPosDevice.vue';
  import ModalsPosDeviceToken from '@/components/modals/pos-device/ModalsPosDeviceToken.vue';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import { copyTextToClipboard } from '@/utils/clipboard.utils';
  import { IPosDevice } from '@/interfaces/IPosDevice';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  const restaurantStore = useRestaurantStore();
  const ui = useUi();
  const modals = useModal();

  const posDevices = ref<IPosDevice[] | null>(null);
  const loading = ref(false);
  const searchFilter = ref('');

  async function loadPosDevices() {
    if (!restaurantStore.restaurant?.id) return;

    try {
      loading.value = true;
      const data = await PosDevicesService.getAll(
        restaurantStore.restaurant.id
      );
      posDevices.value = data;
    } catch (error) {
      ui.serverError(error, 'Error al cargar los dispositivos POS');
    } finally {
      loading.value = false;
    }
  }

  watch(
    () => restaurantStore.restaurant?.id,
    () => {
      loadPosDevices();
    },
    { immediate: true }
  );

  const filteredPosDevices = computed(() => {
    if (!posDevices.value) return null;
    if (!searchFilter.value) return posDevices.value;

    const search = searchFilter.value.toLowerCase();
    return posDevices.value.filter(
      (device) =>
        device.name.toLowerCase().includes(search) ||
        device.token.toLowerCase().includes(search) ||
        (device.branch?.name?.toLowerCase().includes(search) ?? false)
    );
  });

  function openNewPosDeviceDialog() {
    modals.show(
      ModalsPosDevice,
      {},
      {
        name: 'pos-device',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: false,
      },
      {
        saved: (device: IPosDevice) => {
          loadPosDevices();
          // Show token after creation
          showTokenDialog(device);
        },
      }
    );
  }

  function openEditPosDeviceDialog(device: IPosDevice) {
    modals.show(
      ModalsPosDevice,
      { posDeviceToEdit: device },
      {
        name: 'pos-device',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: false,
      },
      {
        saved: () => {
          loadPosDevices();
        },
      }
    );
  }

  function showTokenDialog(device: IPosDevice) {
    modals.show(
      ModalsPosDeviceToken,
      { posDevice: device },
      {
        name: 'pos-device-token',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 500,
        clickToClose: true,
      }
    );
  }

  const deletingDeviceId = ref<number | null>(null);
  async function deletePosDevice(device: IPosDevice) {
    if (deletingDeviceId.value) return;

    const confirmed = await modals.confirm({
      title: 'Eliminar dispositivo POS',
      message: '¿Está seguro de que desea eliminar este dispositivo?',
      acceptText: 'Eliminar',
      cancelText: 'Cancelar',
      acceptColor: 'error',
    });

    if (!confirmed) return;

    try {
      deletingDeviceId.value = device.id;
      await PosDevicesService.delete(restaurantStore.restaurant.id, device.id);
      ui.showToast(AlertType.Success, 'Dispositivo eliminado');
      loadPosDevices();
    } catch (error) {
      ui.serverError(error, 'Error al eliminar el dispositivo');
    } finally {
      deletingDeviceId.value = null;
    }
  }

  // NOTE: Admin unlink has been removed. Unlinking must be done from the POS device itself.

  const regeneratingTokenId = ref<number | null>(null);
  async function regenerateToken(device: IPosDevice) {
    if (regeneratingTokenId.value) return;

    // Only allow token regeneration when device is NOT linked
    if (device.deviceId) {
      ui.showToast(
        AlertType.Warning,
        'No se puede regenerar el token mientras esté vinculado. Desvincule desde el POS primero.'
      );
      return;
    }

    const confirmed = await modals.confirm({
      title: 'Regenerar token',
      message:
        '¿Está seguro de que desea regenerar el token? ' +
        'El token actual dejará de funcionar.',
      acceptText: 'Regenerar',
      cancelText: 'Cancelar',
      acceptColor: 'warning',
    });

    if (!confirmed) return;

    try {
      regeneratingTokenId.value = device.id;
      const updatedDevice = await PosDevicesService.regenerateToken(
        restaurantStore.restaurant.id,
        device.id
      );
      ui.showToast(AlertType.Success, 'Token regenerado');
      loadPosDevices();
      // Show new token
      showTokenDialog(updatedDevice);
    } catch (error) {
      ui.serverError(error, 'Error al regenerar el token');
    } finally {
      regeneratingTokenId.value = null;
    }
  }

  async function toggleEnabled(device: IPosDevice) {
    try {
      await PosDevicesService.update(restaurantStore.restaurant.id, device.id, {
        enabled: !device.enabled,
      });
      ui.showToast(
        AlertType.Success,
        device.enabled ? 'Dispositivo deshabilitado' : 'Dispositivo habilitado'
      );
      loadPosDevices();
    } catch (error) {
      ui.serverError(error, 'Error al actualizar el dispositivo');
    }
  }

  async function copyToken(token: string) {
    try {
      await copyTextToClipboard(token);
      ui.showToast(AlertType.Success, 'Token copiado al portapapeles');
    } catch (e) {
      ui.showToast(AlertType.Error, 'No se pudo copiar el token');
    }
  }

  function getStatusText(device: IPosDevice) {
    if (!device.enabled) return 'Deshabilitado';
    if (!device.deviceId) return 'No vinculado';
    return 'Vinculado';
  }

  function getStatusColor(device: IPosDevice) {
    if (!device.enabled) return 'error';
    if (!device.deviceId) return 'warning';
    return 'success';
  }

  function formatDate(date: Date | null) {
    if (!date) return '-';
    return new Date(date).toLocaleString('es-ES');
  }
</script>

<template>
  <div class="pos-devices-page page-container">
    <div class="page-inner">
      <v-card class="elevation-0">
        <v-card-title>
          <div class="header-container">
            <div class="header-title-container">
              <v-icon>mdi-tablet</v-icon>
              <span>Dispositivos POS</span>
            </div>
            <div class="header-actions-container">
              <v-btn color="success" @click="openNewPosDeviceDialog">
                <v-icon left>mdi-plus</v-icon>
                <span>Nuevo</span>
              </v-btn>
            </div>
          </div>
        </v-card-title>
        <v-card-subtitle>
          Administre los dispositivos POS autorizados para su restaurante
        </v-card-subtitle>
        <v-card-text>
          <div class="pos-devices-list">
            <div class="search-container">
              <v-text-field
                v-model="searchFilter"
                prepend-inner-icon="mdi-magnify"
                label="Buscar dispositivo"
                clearable
                hide-details
                outlined
                dense
              />
            </div>

            <template v-if="!filteredPosDevices">
              <v-skeleton-loader type="card" />
              <v-skeleton-loader type="card" />
            </template>
            <template v-else-if="filteredPosDevices.length === 0">
              <p class="tw-text-center tw-text-gray-500">
                No hay dispositivos registrados
              </p>
            </template>
            <template v-else>
              <div class="devices-grid">
                <v-card
                  v-for="device in filteredPosDevices"
                  :key="device.id"
                  class="device-card"
                  outlined
                >
                  <v-card-title class="device-card-title">
                    <div class="device-name">
                      <v-icon left>mdi-tablet</v-icon>
                      {{ device.name }}
                    </div>
                    <v-chip
                      small
                      :color="getStatusColor(device)"
                      text-color="white"
                    >
                      {{ getStatusText(device) }}
                    </v-chip>
                  </v-card-title>

                  <v-card-text class="device-card-content">
                    <div class="device-info-row">
                      <span class="label">Token:</span>
                      <div class="token-display">
                        <code class="token-code">{{ device.token }}</code>
                        <v-btn
                          x-small
                          icon
                          @click="copyToken(device.token)"
                          title="Copiar token"
                        >
                          <v-icon x-small>mdi-content-copy</v-icon>
                        </v-btn>
                      </div>
                    </div>

                    <div class="device-info-row">
                      <span class="label">Sucursal:</span>
                      <span>{{ device.branch?.name || 'Sin sucursal' }}</span>
                    </div>

                    <div v-if="device.lastLoginAt" class="device-info-row">
                      <span class="label">Último acceso:</span>
                      <span>{{ formatDate(device.lastLoginAt) }}</span>
                    </div>

                    <div v-if="device.lastLoginIp" class="device-info-row">
                      <span class="label">IP:</span>
                      <span>{{ device.lastLoginIp }}</span>
                    </div>
                  </v-card-text>

                  <v-card-actions class="device-card-actions">
                    <v-menu offset-y>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          small
                          text
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                        >
                          Acciones
                          <v-icon right small>mdi-menu-down</v-icon>
                        </v-btn>
                      </template>
                      <v-list dense>
                        <v-list-item @click="showTokenDialog(device)">
                          <v-list-item-icon>
                            <v-icon small>mdi-qrcode</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title>Ver Token</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item @click="openEditPosDeviceDialog(device)">
                          <v-list-item-icon>
                            <v-icon small>mdi-pencil</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title>Editar</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item @click="toggleEnabled(device)">
                          <v-list-item-icon>
                            <v-icon small>
                              {{
                                device.enabled
                                  ? 'mdi-toggle-switch-off'
                                  : 'mdi-toggle-switch'
                              }}
                            </v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title>
                              {{
                                device.enabled ? 'Deshabilitar' : 'Habilitar'
                              }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>

                        <!-- NOTE: Admin unlink has been removed. Unlinking must be done from the POS device itself. -->

                        <v-tooltip left :disabled="!device.deviceId">
                          <template v-slot:activator="{ on, attrs }">
                            <div v-bind="attrs" v-on="on">
                              <v-list-item
                                @click="regenerateToken(device)"
                                :disabled="
                                  regeneratingTokenId === device.id ||
                                  !!device.deviceId
                                "
                              >
                                <v-list-item-icon>
                                  <v-icon small>mdi-refresh</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                  <v-list-item-title
                                    >Regenerar Token</v-list-item-title
                                  >
                                </v-list-item-content>
                              </v-list-item>
                            </div>
                          </template>
                          <span
                            >Desvincule desde el POS para regenerar el
                            token</span
                          >
                        </v-tooltip>

                        <v-divider />

                        <v-list-item
                          @click="deletePosDevice(device)"
                          :disabled="deletingDeviceId === device.id"
                        >
                          <v-list-item-icon>
                            <v-icon small color="error">mdi-delete</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title class="tw-text-red-600">
                              Eliminar
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </v-card-actions>
                </v-card>
              </div>
            </template>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .page-container {
    @apply tw-flex tw-justify-center tw-p-4;

    .page-inner {
      @apply tw-w-full tw-max-w-6xl;

      .header-container {
        @apply tw-flex tw-justify-between tw-items-center tw-w-full;

        .header-title-container {
          @apply tw-flex tw-items-center tw-space-x-1.5;
        }
      }

      .pos-devices-list {
        @apply tw-space-y-4;

        .search-container {
          @apply tw-mb-4;
        }

        .devices-grid {
          @apply tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4;

          .device-card {
            @apply tw-flex tw-flex-col;

            .device-card-title {
              @apply tw-flex tw-justify-between tw-items-center;

              .device-name {
                @apply tw-flex tw-items-center tw-font-medium;
              }
            }

            .device-card-content {
              @apply tw-flex-1 tw-space-y-2;

              .device-info-row {
                @apply tw-flex tw-items-center tw-gap-2 tw-text-sm;

                .label {
                  @apply tw-font-medium tw-text-gray-600 tw-min-w-[120px];
                }

                .token-display {
                  @apply tw-flex tw-items-center tw-gap-1 tw-flex-1;

                  .token-code {
                    @apply tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded tw-font-mono tw-text-xs tw-break-all;
                  }
                }
              }
            }

            .device-card-actions {
              @apply tw-border-t tw-pt-2;
            }
          }
        }
      }
    }
  }
</style>
